import { Controller, useForm } from 'react-hook-form';
import styled from 'styled-components';
import { Input } from '../../../components/items/joinInput';
import DatePicker from 'react-datepicker';
import { ko } from 'date-fns/locale';
import {
  Calendar,
  DonwArrowIcon,
  SettingIcon,
} from '../../../components/icon/icon';
import { useEffect, useState } from 'react';
import Select from 'react-select';
import {
  formatLocalDate,
  formatNumber,
  formatPointNumber,
  formatTime,
} from '../../../utils/util';
import { UserInputSetting } from '../../../components/user/dairy/user.input.setting';
import { AnimatePresence, motion } from 'framer-motion';
import { useHistory, useLocation } from 'react-router-dom';
import { RadioButtonInput } from '../../../components/items/radio.input';
import { defaultValuesRent } from '../../../utils/reportDefaultValues';
import { zodResolver } from '@hookform/resolvers/zod';
import { reportRentRegistSchema } from '../../../schema/reportSchema';
import { AlertModal } from '../../../components/modal/alert.modal';
import { fetchDriverAmount } from '../../../api/mypage';
import {
  getCorporationList,
  reportRegistrationRent,
} from '../../../api/report';

const workTypeSelectItems = [
  { value: 'full', label: '전일' },
  { value: 'half', label: '반일' },
];

const radioBtnItems = [
  { key: '0', value: '루베' },
  { key: '1', value: '회전수' },
];

interface LocationState {
  selectedType?: string;
  borderLine?: boolean;
}

interface RegistrationReportRentDto {
  ryubeiAmount: number; // 루베 금액 (필수)
  cycleAmount: number; // 회전 금액 (필수)
  freshWaterAmount?: number; // 청수 금액 (선택)
  wasteWaterAmount: number; // 폐수 금액 (필수)
  delayAmount: number; // 지연 금액 (필수)
  corporationId: number; // 회사 아이디 (필수)
  arrivedAt: string; // 도착 시 (필수)
  goWorkTime: string; // 출근 시간 (필수)
  leaveWorkTime: string; // 퇴근 시간 (필수)
  dayType: string; // 작업 형태 (필수)
  ryubeiVolume?: number; // 루베량 (선택)
  cycleVolume?: number; // 회전수 (선택)
  operationAmount: number; // 운반비 (필수)
  freshWaterVolume?: number; // 청수 횟수 (선택)
  wasteWaterVolume?: number; // 폐수 횟수 (선택)
  delayVolume?: number; // 지연 횟수 (선택)
  lkmStandard?: number; // L/KM 기준값 (선택)
  lkmAmount?: number; // L/KM 기준 금액 (선택)
  lkmDistance?: number; // L/KM 거리 (선택)
  kmlStandard?: number; // KM/L 기준값 (선택)
  kmlAmount?: number; // KM/L 기준 금액 (선택)
  kmlDistance?: number; // KM/L 거리 (선택)
  ausAmount?: number; // 요소수 금액 (선택)
  ausVolume?: number; // 요소수 주유량 (선택)
  refuelingAmount?: number; // 주유 가격 (선택)
  refuelingVolume?: number; // 주유량 (선택)
  additionAmount?: number; // 추가 작업 금액 (선택)
  additionComment?: string; // 추가 작업 내용 (선택)
  totalAmount: number; // 총 수입 금액 (필수)

  // 추가
  lkmTotalAmount: number;
  kmlTotalAmount: number;
  ausTotalAmount: number;

  // 추가
  freshWaterTotalAmount: number;
  refuelingTotalAmount: number;
  wasteWaterTotalAmount: number;
  delayTotalAmount: number;
  autoTotalAmount: number;
}

interface CorporationListDto {
  corporationId: number;
  corporationName: string;
}

const UserDiaryRentRegist = () => {
  const { state } = useLocation<LocationState>();
  const selectType = state.selectedType;
  const today = new Date();

  const defaultValues = defaultValuesRent;

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<RegistrationReportRentDto>({
    defaultValues,
    resolver: zodResolver(reportRentRegistSchema),
  });

  const ryubieAmount = watch('ryubeiAmount');
  const ryubeiVolume = watch('ryubeiVolume');
  const cycleAmount = watch('cycleAmount');
  const cycleVolume = watch('cycleVolume');

  const freshWaterAmount = watch('freshWaterAmount'); // 청수 금액
  const freshWaterVolume = watch('freshWaterVolume'); // 청수 횟수

  const wasteWaterAmount = watch('wasteWaterAmount'); // 폐수 금액
  const wasteWaterVolume = watch('wasteWaterVolume'); // 폐수 횟수

  const delayAmount = watch('delayAmount'); // 지연 금액
  const delayVolume = watch('delayVolume'); // 지연 횟수

  const lkmStandard = watch('lkmStandard'); // lkm 금액
  const lkmAmount = watch('lkmAmount'); // lkm 금액
  const lkmDistance = watch('lkmDistance'); // lkm 횟수

  const kmlStandard = watch('kmlStandard'); // kml 금액
  const kmlAmount = watch('kmlAmount'); // kml 금액
  const kmlDistance = watch('kmlDistance'); // kml 횟수

  const ausAmount = watch('ausAmount'); // 요소수 금액
  const ausVolume = watch('ausVolume'); // 요소수 주유량

  const refuelingAmount = watch('refuelingAmount'); // 주유 금액
  const refuelingVolume = watch('refuelingVolume'); // 주유량

  const additionAmount = watch('additionAmount'); // 추가 작업 금액
  const operationAmount = watch('operationAmount'); // 운반비
  const freshWaterTotalAmount = watch('freshWaterTotalAmount'); // 청수 최종 금액
  const wasteWaterTotalAmount = watch('wasteWaterTotalAmount'); // 폐수 최종 금액
  const delayTotalAmount = watch('delayTotalAmount'); // 지연 최종 금액

  const [targetDate, setTargetDate] = useState<Date>(today);
  const [startTime, setStartTime] = useState<Date>(new Date());
  const [endTime, setEndTime] = useState<Date>(new Date());
  const [etcSettingOpen, setEtcSettingOpen] = useState(false);
  const [switchStates, setSwitchStates] = useState<boolean[]>([]); // 추가된 상태
  const [selectedRadio, setSelectedRadio] = useState('0'); // 초기값 설정

  // 모달
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMessage, setModalMessage] = useState('');
  const [navigateOnClose, setNavigateOnClose] = useState(false); // 페이지 이동 여부를 추적

  const [corporationList, setCorporationList] = useState<CorporationListDto[]>(
    [],
  );

  const handleModalClose = () => {
    setIsModalOpen(false);
    if (navigateOnClose) {
      history.push({
        pathname: '/userDiary',
      });
    }
  };

  const handleSettingOpen = () => {
    setEtcSettingOpen(true);
  };
  const handleSettingClose = () => {
    setEtcSettingOpen(false);
  };

  const handleSaveSettings = (settings: boolean[]) => {
    setSwitchStates(settings); // 스위치 상태값 저장
    console.log('settings:', settings);
  };

  const handleRadioChange = (e: any) => {
    setSelectedRadio(e.target.value); // 라디오 버튼 값 변경 시 상태 업데이트
  };

  useEffect(() => {
    const getStandardAmount = async () => {
      try {
        const data = await fetchDriverAmount();
        console.log(data);
        // halfDayAmount가 0이면 설정하지 않음
        setValue('ryubeiAmount', Number(data.ryubei));
        setValue('cycleAmount', Number(data.cycle));
        setValue('freshWaterAmount', Number(data.freshWater));
        setValue('wasteWaterAmount', Number(data.wasteWater));
        setValue('delayAmount', Number(data.delay));
      } catch (error) {
        console.error(error);
      }
    };

    getStandardAmount();
  }, []);

  const onSubmit = async (data: RegistrationReportRentDto) => {
    const formData = {
      ...data,
      arrivedAt: data.arrivedAt
        ? formatLocalDate(new Date(data.arrivedAt))
        : '',
      goWorkTime: data.goWorkTime ? formatTime(new Date(data.goWorkTime)) : '',
      leaveWorkTime: data.leaveWorkTime
        ? formatTime(new Date(data.leaveWorkTime))
        : '',
    };

    try {
      const response = await reportRegistrationRent(formData);
      if (response.statusCode === 200) {
        setModalMessage(response.message);
        setIsModalOpen(true);
        setNavigateOnClose(true);
      } else {
        setModalMessage('일보 - 마당차 등록에 실패했습니다.');
        setIsModalOpen(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setValue('operationAmount', 0);

    if (selectedRadio === '0') {
      setValue('operationAmount', Number(ryubieAmount) * Number(ryubeiVolume));
    } else if (selectedRadio === '1') {
      setValue('operationAmount', Number(cycleAmount) * Number(cycleVolume));
    }
  }, [ryubieAmount, ryubeiVolume, selectedRadio, cycleAmount, cycleVolume]); // dayType, fullDayAmount,

  useEffect(() => {
    setValue(
      'freshWaterTotalAmount',
      Number(freshWaterAmount) * Number(freshWaterVolume),
    ); // 청수 총 금액
    setValue(
      'wasteWaterTotalAmount',
      Number(wasteWaterAmount) * Number(wasteWaterVolume),
    ); // 폐수 총 금액
    setValue('delayTotalAmount', Number(delayAmount) * Number(delayVolume)); // 지연 총 금액
    setValue(
      'lkmTotalAmount',
      Number(lkmStandard) * Number(lkmAmount) * Number(lkmDistance),
    ); // lkm 총 금액
    setValue(
      'kmlTotalAmount',
      Number(kmlStandard) * Number(kmlAmount) * Number(kmlDistance),
    ); // kml 총 금액
    setValue('ausTotalAmount', Number(ausAmount) * Number(ausVolume)); // 요소수 총 금액
    setValue(
      'refuelingTotalAmount',
      Number(refuelingAmount) * Number(refuelingVolume),
    ); // 주유 총 금액
  }, [
    freshWaterVolume,
    wasteWaterVolume,
    delayVolume,
    lkmStandard,
    lkmDistance,
    lkmAmount,
    kmlAmount,
    kmlDistance,
    kmlStandard,
    ausAmount,
    ausVolume,
    refuelingAmount,
    refuelingVolume,
  ]);

  useEffect(() => {
    setValue(
      'autoTotalAmount',
      Number(operationAmount) +
        Number(additionAmount) +
        Number(freshWaterTotalAmount) +
        Number(wasteWaterTotalAmount) +
        Number(delayTotalAmount),
    );
    setValue(
      'totalAmount',
      Number(operationAmount) +
        Number(additionAmount) +
        Number(freshWaterTotalAmount) +
        Number(wasteWaterTotalAmount) +
        Number(delayTotalAmount),
    );
  }, [
    operationAmount,
    additionAmount,
    freshWaterTotalAmount,
    wasteWaterTotalAmount,
    delayTotalAmount,
  ]);

  useEffect(() => {
    const getCorporationListData = async () => {
      try {
        const response = await getCorporationList();
        setCorporationList(response);
      } catch (error) {
        console.error(error);
      }
    };

    getCorporationListData();
  }, []);
  return (
    <Container>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="select-wrapper">
          <span>회사명</span>
          <Controller
            name="corporationId"
            control={control}
            render={({ field }) => (
              <Select
                className="search-select"
                classNamePrefix="react-select"
                options={corporationList.map((corp) => ({
                  value: corp.corporationId, // corporationName을 value로 설정
                  label: corp.corporationName, // corporationName을 label로 설정
                }))}
                onChange={(selectedOption) =>
                  field.onChange(selectedOption ? selectedOption.value : null)
                }
                value={corporationList
                  .map((corp) => ({
                    value: corp.corporationId,
                    label: corp.corporationName,
                  }))
                  .find((option) => option.value === field.value)} // corporationName을 비교하여 현재 선택된 값을 설정
                components={{ IndicatorSeparator: () => null }}
                placeholder="회사 선택"
                menuPlacement="top"
                maxMenuHeight={100}
              />
            )}
          />
        </div>
        {errors.corporationId && (
          <ValTxt>{String(errors.corporationId.message)}</ValTxt>
        )}
        <DatePickerContainer>
          <span>도착일</span>
          <div className="inner">
            <Controller
              name="arrivedAt"
              control={control}
              render={({ field: { onChange, value } }) => (
                <CustomDatePicker
                  className="search-input"
                  locale={ko}
                  dateFormat="yyyy-MM-dd"
                  selected={value ? new Date(value) : null} // 문자열을 Date 객체로 변환
                  startDate={targetDate}
                  selectsStart
                  onChange={(date: Date | null) => onChange(date)}
                  placeholderText="날짜 선택"
                  withPortal
                />
              )}
            />
            <div className="icon-box">
              <Calendar />
            </div>
          </div>
        </DatePickerContainer>
        {errors.arrivedAt && (
          <ValTxt>{String(errors.arrivedAt.message)}</ValTxt>
        )}
        <span className="set-cost-title">기준 금액 설정</span>
        <div className="row-50">
          <div className="cost-wrapper">
            <Controller
              name="ryubeiAmount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="루베 금액"
                  name="ryubeiAmount"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="금액입력"
                />
              )}
            />
          </div>
          <div className="cost-wrapper">
            <Controller
              name="cycleAmount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="회전 금액"
                  name="cycleAmount"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder={formatNumber(720000)}
                />
              )}
            />
          </div>
        </div>
        <div className="row-50">
          <div className="cost-wrapper">
            <Controller
              name="freshWaterAmount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="청수 금액"
                  name="freshWaterAmount"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="금액입력"
                />
              )}
            />
          </div>
          <div className="cost-wrapper">
            <Controller
              name="wasteWaterAmount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="폐수 금액"
                  name="wasteWaterAmount"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="금액입력"
                />
              )}
            />
          </div>
        </div>
        <div className="row-50">
          <div className="cost-wrapper">
            <Controller
              name="delayAmount"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="지연 금액"
                  name="delayAmount"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="금액입력"
                />
              )}
            />
          </div>
        </div>

        <div className="row-50">
          <DatePickerContainer>
            <span>출근시간</span>
            <div className="inner">
              <Controller
                name="goWorkTime"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    className="search-input"
                    selected={value ? new Date(value) : null}
                    onChange={(date: any) => onChange(date)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="출근시간"
                    dateFormat="hh:mm aa"
                    withPortal
                    placeholderText={startTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })} // 문자열로 변환
                  />
                )}
              />
              <div className="icon-box">
                <DonwArrowIcon />
              </div>
            </div>
          </DatePickerContainer>
          <DatePickerContainer>
            <span>퇴근시간</span>
            <div className="inner">
              <Controller
                name="leaveWorkTime"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <CustomDatePicker
                    className="search-input"
                    selected={value ? new Date(value) : null}
                    onChange={(date: any) => onChange(date)}
                    showTimeSelect
                    showTimeSelectOnly
                    timeIntervals={15}
                    timeCaption="퇴근시간"
                    dateFormat="hh:mm aa"
                    withPortal
                    placeholderText={endTime.toLocaleTimeString('en-US', {
                      hour: '2-digit',
                      minute: '2-digit',
                    })} // 문자열로 변환
                  />
                )}
              />
              <div className="icon-box">
                <DonwArrowIcon />
              </div>
            </div>
          </DatePickerContainer>
        </div>
        {errors.goWorkTime && (
          <ValTxt>{String(errors.goWorkTime.message)}</ValTxt>
        )}
        {errors.leaveWorkTime && (
          <ValTxt>{String(errors.leaveWorkTime.message)}</ValTxt>
        )}
        <div className="select-wrapper">
          <span>작업형태</span>
          <Controller
            name="dayType"
            control={control}
            render={({ field }) => (
              <Select
                className="search-select"
                classNamePrefix="react-select"
                options={workTypeSelectItems}
                onChange={(val) => field.onChange(val ? val.value : null)}
                value={workTypeSelectItems.find((c) => c.value === field.value)}
                components={{ IndicatorSeparator: () => null }}
                placeholder="작업 형태 선택"
                maxMenuHeight={200}
              />
            )}
          />
        </div>
        {errors.dayType && <ValTxt>{String(errors.dayType.message)}</ValTxt>}
        <div className="radio-box-wrapper">
          <RadioButtonInput
            items={radioBtnItems}
            register={register}
            registerName="madangRadioValue"
            onChange={handleRadioChange} // 라디오 버튼 변경 시 핸들러 호출
          />
        </div>
        <div className="row-50">
          <div className={`wrapper ${selectedRadio !== '0' ? 'disabled' : ''}`}>
            <Controller
              name="ryubeiVolume"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="루베"
                  name="ryubeiVolume"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="m3 단위 입력"
                  disabled={selectedRadio !== '0'} // 선택된 라디오 버튼에 따라 비활성화
                />
              )}
            />
          </div>
          <div className={`wrapper ${selectedRadio !== '1' ? 'disabled' : ''}`}>
            <Controller
              name="cycleVolume"
              control={control}
              render={({ field: { onChange, value } }) => (
                <Input
                  label="회전수"
                  name="cycleVolume"
                  value={value ? formatNumber(Number(value)) : ''}
                  onChange={(e) => {
                    const rawValue = e.target.value.replace(/[^0-9]/g, '');
                    onChange(rawValue);
                  }}
                  placeholder="숫자 입력"
                  disabled={selectedRadio !== '1'} // 선택된 라디오 버튼에 따라 비활성화
                />
              )}
            />
          </div>
        </div>
        <div className="wrapper">
          <Controller
            name="operationAmount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label="운반비"
                name="operationAmount"
                value={value ? formatNumber(Number(value)) : ''}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  onChange(rawValue);
                }}
                placeholder="자동 계산"
              />
            )}
          />
        </div>
        {errors.operationAmount && (
          <ValTxt>{String(errors.operationAmount.message)}</ValTxt>
        )}
        <div className="etc-setting-btn-box">
          <button type="button" onClick={handleSettingOpen}>
            <span>입력 설정</span>
            <span className="icon-box">
              <SettingIcon />
            </span>
          </button>
        </div>
        {switchStates[0] ? (
          <div className="row-50">
            <div className="cost-wrapper">
              <Controller
                name="freshWaterVolume"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="청수 (물차) 횟수"
                    name="freshWaterVolume"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder={formatNumber(1)}
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="freshWaterTotalAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="청수 (물차) 최종 금액"
                    name="freshWaterTotalAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="자동 계산"
                  />
                )}
              />
            </div>
          </div>
        ) : null}
        {switchStates[1] ? (
          <div className="row-50">
            <div className="cost-wrapper">
              <Controller
                name="wasteWaterVolume"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="폐수 횟수"
                    name="wasteWaterVolume"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder={formatNumber(1)}
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="wasteWaterTotalAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="폐수 최종 금액"
                    name="wasteWaterTotalAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="자동 계산"
                  />
                )}
              />
            </div>
          </div>
        ) : null}
        {switchStates[2] ? (
          <div className="row-50">
            <div className="cost-wrapper">
              <Controller
                name="delayVolume"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="지연 횟수"
                    name="delayVolume"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder={formatNumber(1)}
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="delayTotalAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="지연 최종 금액"
                    name="delayTotalAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="자동 계산"
                  />
                )}
              />
            </div>
          </div>
        ) : null}
        {switchStates[3] ? (
          <div className="row-50">
            <div className="cost-wrapper">
              <Controller
                name="lkmStandard"
                control={control}
                render={({ field: { onChange, onBlur, value } }) => (
                  <Input
                    label="L/Km 기준값"
                    name="lkmStandard"
                    value={value === 0 ? '' : value || ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9.]/g, ''); // 소수점과 숫자만 허용
                      onChange(rawValue); // 사용자 입력을 그대로 반영
                    }}
                    onBlur={(e) => {
                      const formattedValue = formatPointNumber(
                        e.target.value,
                        2,
                      ); // 포맷팅 적용
                      onChange(formattedValue);
                    }}
                    placeholder={formatPointNumber(1, 2)} // placeholder는 포맷팅 적용된 값
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="lkmAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="L/Km 기준 금액"
                    name="lkmAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="숫자 입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="lkmDistance"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="운행거리"
                    name="lkmDistance"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="운행거리 입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="lkmTotalAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="받을 금액"
                    name="lkmTotalAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="자동 계산"
                  />
                )}
              />
            </div>
          </div>
        ) : null}
        {switchStates[4] ? (
          <div className="row-50">
            <div className="cost-wrapper">
              <Controller
                name="kmlStandard"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="Km/L 기준값"
                    name="kmlStandard"
                    value={value === 0 ? '' : value || ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9.]/g, ''); // 소수점과 숫자만 허용
                      onChange(rawValue); // 사용자 입력을 그대로 반영
                    }}
                    onBlur={(e) => {
                      const formattedValue = formatPointNumber(
                        e.target.value,
                        2,
                      ); // 포맷팅 적용
                      onChange(formattedValue);
                    }}
                    placeholder={formatPointNumber(1, 2)} // placeholder는 포맷팅 적용된 값
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="kmlAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="Km/L 기준금액"
                    name="kmlAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="금액 입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="kmlDistance"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="운행거리"
                    name="kmlDistance"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="운행거리 입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="kmlTotalAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="받을 금액"
                    name="kmlTotalAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="자동 계산"
                  />
                )}
              />
            </div>
          </div>
        ) : null}
        {switchStates[5] ? (
          <div className="row-50">
            <div className="cost-wrapper">
              <Controller
                name="ausAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="요소수 금액"
                    name="ausAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="금액입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="ausVolume"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="요소수 주유량"
                    name="ausVolume"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="주유량 입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="ausTotalAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="요소수 최종 금액"
                    name="ausTotalAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="자동 계산"
                  />
                )}
              />
            </div>
          </div>
        ) : null}
        {switchStates[6] ? (
          <div className="row-50">
            <div className="cost-wrapper">
              <Controller
                name="refuelingAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="실제 주유 가격/L"
                    name="refuelingAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="금액입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="refuelingVolume"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="실제 주유량(L)"
                    name="refuelingVolume"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="주유량 입력"
                  />
                )}
              />
            </div>
            <div className="cost-wrapper">
              <Controller
                name="refuelingTotalAmount"
                control={control}
                render={({ field: { onChange, value } }) => (
                  <Input
                    label="주유 금액"
                    name="refuelingTotalAmount"
                    value={value ? formatNumber(Number(value)) : ''}
                    onChange={(e) => {
                      const rawValue = e.target.value.replace(/[^0-9]/g, '');
                      onChange(rawValue);
                    }}
                    placeholder="자동 계산"
                  />
                )}
              />
            </div>
          </div>
        ) : null}
        <Input
          type="text"
          label="추가 작업 내용"
          placeholder="내용 입력"
          {...register('additionComment')}
        />
        <div className="wrapper">
          <Controller
            name="additionAmount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label="추가 작업 금액"
                name="additionAmount"
                value={value ? formatNumber(Number(value)) : ''}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  onChange(rawValue);
                }}
                placeholder="추가 작업 금액 입력"
              />
            )}
          />
        </div>
        <div className="wrapper">
          <Controller
            name="autoTotalAmount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label="자동 계산 금액 (수정 불가)"
                name="autoTotalAmount"
                value={value ? formatNumber(Number(value)) : ''}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  onChange(rawValue);
                }}
                placeholder="자동 계산"
                readOnly
              />
            )}
          />
        </div>
        <div className="wrapper">
          <Controller
            name="totalAmount"
            control={control}
            render={({ field: { onChange, value } }) => (
              <Input
                label="총 수익 금액 (수정 가능)"
                name="totalAmount"
                value={value ? formatNumber(Number(value)) : ''}
                onChange={(e) => {
                  const rawValue = e.target.value.replace(/[^0-9]/g, '');
                  onChange(rawValue);
                }}
                placeholder="자동 계산"
              />
            )}
          />
        </div>
        <ConfirmBtn onClick={() => {}}>등록 하기</ConfirmBtn>
      </form>
      <AnimatePresence initial={false}>
        {etcSettingOpen ? (
          <SearchConditionWrap
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{
              type: 'fade',
            }}
          >
            <UserInputSetting
              handleSearchClose={handleSettingClose}
              onSaveSettings={handleSaveSettings}
              initialSwitchStates={switchStates}
              selectType={selectType}
            />
          </SearchConditionWrap>
        ) : null}
      </AnimatePresence>

      {isModalOpen && (
        <AlertModal
          handleConfirmClose={handleModalClose}
          confirmText={modalMessage}
          onClick={handleModalClose}
        />
      )}
    </Container>
  );
};

export default UserDiaryRentRegist;

// style
const Container = styled.div`
  padding: 1.5rem 2.2rem;
  height: calc(100% - 9rem);
  overflow: scroll;
  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }
  form {
    display: flex;
    flex-direction: column;
    gap: 1.2rem;
    .wrapper {
      &.disabled {
        position: relative;
        &::after {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          width: 100%;
          height: 100%;
          opacity: 0.2;
          z-index: 1;
          background-color: ${(props) => props.theme.gray500};
          border-radius: 0.8rem;
        }
      }
    }
    .select-wrapper {
      padding: 1.1rem 1.1rem 1.6rem 1.5rem;
      background-color: ${(props) => props.theme.gray100};
      border-radius: 0.8rem;
      display: flex;
      flex-direction: column;
      font-size: 1.6rem;
      font-weight: 600;
      span {
        font-size: 1.2rem;
        font-weight: 600;
        color: ${(props) => props.theme.joinLabelColor};
        margin-bottom: 0.6rem;
        line-height: 1.5rem;
      }
      .search-select {
        .react-select__placeholder {
          color: ${(props) => props.theme.placeholderColor};
          font-weight: 400;
        }
        .react-select__control {
          background-color: transparent;
          min-height: 2rem;
          height: 2rem;
          border: none;
          .react-select__value-container {
            padding: 0;
          }
        }
        .react-select__indicator {
          padding: 0;
        }
      }
    }
    .set-cost-title {
      margin: 0.4rem 1rem;
      font-size: 1.4rem;
      font-weight: 600;
    }
    .cost-wrapper {
      div {
        > div {
          background-color: ${(props) => props.theme.costBgColor};
        }
      }
    }
    .row-50 {
      display: grid;
      grid-template-columns: repeat(2, 1fr);
      gap: 0.9rem;
      .cost-wrapper {
        div {
          > div {
            background-color: ${(props) => props.theme.costBgColor};
          }
        }
      }
    }
    .etc-setting-btn-box {
      display: flex;
      button {
        padding: 1.8rem 1rem;
        outline: none;
        border: none;
        background-color: transparent;
        display: flex;
        align-items: center;
        gap: 1.2rem;
        span {
          font-size: 1.6rem;
          font-weight: 600;
          line-height: 2rem;
        }
        .icon-box {
          display: flex;
        }
      }
    }
  }
  .radio-box-wrapper {
    padding: 0.3rem 0;
  }
`;

const ConfirmBtn = styled.button`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 100%;
  min-height: 9.4rem;
  border: none;
  outline: none;
  padding: 1.7rem;
  display: flex;
  align-items: flex-start;
  justify-content: center;
  background-color: ${(props) => props.theme.btnStrongBlueColor};
  color: ${(props) => props.theme.textWhiteColor};
  font-size: 1.8rem;
  font-weight: 600;
`;

const CustomDatePicker = styled(DatePicker)``;

const DatePickerContainer = styled.div`
  padding: 1.1rem 1.1rem 1.6rem 1.5rem;
  background-color: ${(props) => props.theme.gray100};
  border-radius: 0.8rem;
  display: flex;
  flex-direction: column;
  span {
    font-size: 1.2rem;
    font-weight: 600;
    color: ${(props) => props.theme.joinLabelColor};
    margin-bottom: 0.6rem;
    line-height: 1.5rem;
  }
  .inner {
    position: relative;
    width: 100%;
    min-height: 2.4rem;
    display: flex;
    align-items: flex-end;
    .search-input {
      width: 100%;
      font-size: 1.6rem;
      font-weight: 400;
      background-color: inherit;
      border: 0px;
      outline: none;
      &::placeholder {
        color: ${(props) => props.theme.placeholderColor};
      }
    }
    .react-datepicker {
      font-size: 1.4rem;
      font-weight: 400;
      .react-datepicker__header {
        background-color: ${(props) => props.theme.textWhiteColor};
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box {
        width: 20rem;
      }
      .react-datepicker__time-container {
        width: 20rem;
      }
      .react-datepicker__time-container
        .react-datepicker__time
        .react-datepicker__time-box
        ul.react-datepicker__time-list
        li.react-datepicker__time-list-item {
        width: 100%;
        height: 4.2rem;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 1.8rem;
      }
      .react-datepicker__current-month,
      .react-datepicker-time__header,
      .react-datepicker-year-header {
        font-size: 1.4rem;
        height: 20px;
        display: flex;
        align-items: center;
        justify-content: center;
      }
      .react-datepicker__day-name,
      .react-datepicker__day,
      .react-datepicker__time-name {
        width: 3rem;
        line-height: 3rem;
      }
      .react-datepicker__day--selected,
      .react-datepicker__day--in-selecting-range,
      .react-datepicker__day--in-range,
      .react-datepicker__month-text--selected,
      .react-datepicker__month-text--in-selecting-range,
      .react-datepicker__month-text--in-range,
      .react-datepicker__quarter-text--selected,
      .react-datepicker__quarter-text--in-selecting-range,
      .react-datepicker__quarter-text--in-range,
      .react-datepicker__year-text--selected,
      .react-datepicker__year-text--in-selecting-range,
      .react-datepicker__year-text--in-range {
        border-radius: 2rem;
        background-color: ${(props) => props.theme.btnBlueColor};
        color: ${(props) => props.theme.textWhiteColor};
      }
    }
    .react-datepicker-popper[data-placement='bottom']
      .react-datepicker__triangle {
      fill: ${(props) => props.theme.textWhiteColor};
      color: ${(props) => props.theme.textWhiteColor};
    }
    .icon-box {
      display: flex;
      position: absolute;
      top: 50%;
      right: 0.1rem;
      transform: translateY(-50%);
      svg {
        path {
          fill: #d7dbde;
        }
      }
    }
  }
`;

const SearchConditionWrap = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 1000;
  display: 'flex';
  justify-content: 'center';
  align-items: 'center';
  cursor: 'pointer';
  width: 100%;
  height: 100%;
  .overlay {
    display: flex;
    width: 100%;
    height: 100%;
    position: absolute;
    top: 0;
    left: 0;
  }
`;

const ValTxt = styled.span`
  color: ${(props) => props.theme.errorColor};
  font-size: 1.2rem;
  font-weight: 400;
  display: flex;
  margin: 0 1.7rem 0;
`;
